// React import
import React, { useState, useRef } from 'react';

// Generic imports
import _ from 'lodash';
import { useDrop } from 'react-dnd';

// Icons
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faInfoCircle, faTrash, faShuffle } from '@fortawesome/pro-light-svg-icons';

// MUI imports
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import { red, grey } from '@mui/material/colors';
import { useTheme } from '@mui/material/styles';

// Custom components
import ProfitWidget from '../components/ProfitWidget.js';

function SectionOfferHeader(props) {
    const { multipleOffers, sectionOffer, sections, offerIndex, sectionIndex, sectionOfferIndex, 
        setModal, products, exchangeRates, buyingRates, transportationMethod, locked, onAddSection, lockedExchangeRates, 
        organization, offers, setOffers } = props;
    const [ isEditingDescription, setIsEditingDescription ] = useState(false);
    const [ editingDescriptionValue, setEditingDescriptionValue ] = useState('');
    
    const theme = useTheme();

    const [ dropOver, setDropOver ] = useState(null);
    const productsRef = useRef();
    productsRef.current = products;

    const first = useRef();
    const sectionRef = useRef();
    sectionRef.current = sections[sectionIndex];
    const setDropOverRef = useRef();
    setDropOverRef.current = setDropOver;
    const onAddSectionRef = useRef();
    onAddSectionRef.current = onAddSection;

    const [ , drop ] = useDrop(() => ({ 
        accept: ['RateCardSingleSection', 'RateCard'],
        hover: (item, monitor) => {
            if (first.current) return;
            first.current = true;
            setDropOverRef.current(monitor.canDrop(item, monitor) ? 'candrop' + (item.rate.type === 'tariff' ? '-tariff' : '') : 'cantdrop' + (item.rate.type === 'tariff' ? '-tariff' : ''));
            function isOver() {
                if (monitor.isOver()) {
                    setTimeout(isOver, 100);
                } else {
                    first.current = false;
                    setDropOverRef.current(null);
                }
            }
            setTimeout(isOver, 100);
        },
        drop: (item, monitor) => {
            if (item.section) {
                onAddSectionRef.current({rate: item.rate, section: item.section });
            } else {
                const _section = item.rate.offer.sections.find(s => s.title === sectionRef.current.title);
                onAddSectionRef.current({rate: item.rate, section: _section });
            }
        },
        canDrop: (item, monitor) => {
            return _.some(productsRef.current, p => {
                const _type = item.rate.product.type;
                const _type2 = p.type;

                if (_type !== _type2) return false;
                if ((item.rate.product.dangerous || false) !== (p.dangerous || false)) return false;
                if (!item.section && !item.rate.offer.sections.find(s => s.title === sectionRef.current.title)) return false;
                return true;
            });
        },
    }));

    return (
        <Box sx={{ padding: '0px 6px', position: 'relative' }} ref={drop}>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                {multipleOffers && (
                    <Box>
                        {sectionOffer.title || <i>New offer</i>}
                    </Box>
                )}
                {multipleOffers && !locked && (
                    <IconButton
                        color='primary'
                        size='small'
                        sx={{ marginLeft: '4px' }}
                        onClick={() => {
                            setModal({
                                title: 'Set Offers\'s Title',
                                label: 'Title',
                                value: sectionOffer.title,
                                path: `[${offerIndex}].sections[${sectionIndex}].offers[${sectionOfferIndex}].title`,
                                open: true,
                            });
                        }}
                    >
                        <FontAwesomeIcon icon={faEdit} />
                    </IconButton>
                )}
                <Box sx={{ flex: 1 }} />
            </Box>
            {multipleOffers && (
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', marginTop: '4px', marginBottom: '8px' }}>
                    {isEditingDescription && !locked ? (
                        <TextField
                            autoFocus
                            value={editingDescriptionValue}
                            onChange={(e) => setEditingDescriptionValue(e.target.value)}
                            onBlur={() => {
                                if (setOffers && offers) {
                                    const newOffers = _.cloneDeep(offers);
                                    _.set(newOffers, `[${offerIndex}].sections[${sectionIndex}].offers[${sectionOfferIndex}].description`, editingDescriptionValue);
                                    setOffers(newOffers);
                                } else {
                                    setModal({
                                        title: 'Set Offer\'s Description',
                                        label: 'Description',
                                        value: editingDescriptionValue,
                                        path: `[${offerIndex}].sections[${sectionIndex}].offers[${sectionOfferIndex}].description`,
                                        open: true,
                                    });
                                }
                                setIsEditingDescription(false);
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.target.blur();
                                } else if (e.key === 'Escape') {
                                    setEditingDescriptionValue(sectionOffer.description || '');
                                    setIsEditingDescription(false);
                                }
                            }}
                            variant='standard'
                            placeholder='You can add a description to this offer here.'
                            sx={{ flex: 1, minWidth: '200px' }}
                        />
                    ) : (
                        <Box
                            sx={{
                                fontWeight: 400,
                                fontSize: '13px',
                                color: theme.palette.text.secondary,
                                cursor: locked ? 'default' : 'pointer',
                                '&:hover': locked ? {} : { textDecoration: 'underline' }
                            }}
                            onClick={() => {
                                if (!locked) {
                                    setEditingDescriptionValue(sectionOffer.description || '');
                                    setIsEditingDescription(true);
                                }
                            }}
                        >
                            {sectionOffer.description || <i style={{ opacity: 0.6 }}>You can add a description to this offer here.</i>}
                        </Box>
                    )}
                </Box>
            )}
            {multipleOffers && (
                <ProfitWidget
                    text={'Choice'}
                    section={sections[sectionIndex]}
                    sectionOffer={sectionOffer}
                    sections={sections}
                    products={products}
                    exchangeRates={exchangeRates}
                    buyingRates={buyingRates}
                    transportationMethod={transportationMethod}
                    lockedExchangeRates={lockedExchangeRates}
                    organization={organization}
                />
            )}
            {dropOver === 'candrop' && (
                <Box sx={{ position: 'absolute', background: grey[800], opacity: 0.8, top: '0px', left: '0px', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', textAlign: 'center' }}>
                    Drop your rate here to update this section offer with its fields.
                </Box>
            )}
            {dropOver === 'candrop-tariff' && (
                <Box sx={{ position: 'absolute', background: grey[800], opacity: 0.8, top: '0px', left: '0px', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', textAlign: 'center' }}>
                    Drop your tariff here to update this section offer with its fields.
                </Box>
            )}
            {dropOver === 'cantdrop' && (
                <Box sx={{ position: 'absolute', background: red[500], opacity: 0.8, top: '0px', left: '0px', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', textAlign: 'center' }}>
                    Rate is for a product not included in the opportunity or does not include '{sections[sectionIndex].title}'.
                </Box>
            )}
            {dropOver === 'cantdrop-tariff' && (
                <Box sx={{ position: 'absolute', background: red[500], opacity: 0.8, top: '0px', left: '0px', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', textAlign: 'center' }}>
                    Tariff is for a product not included in the opportunity or does not include '{sections[sectionIndex].title}'.
                </Box>
            )}
        </Box>
    );
}

export default SectionOfferHeader;
